/**
 * Atoms for Evidence Chips multi-chat state management.
 *
 * Tracks chip-to-chat mappings and active Evidence chat tabs per session.
 */

import type { PinnedChip } from '@/components/ui/floating-ask-heidi';
import type {
  SuggestedChip,
  SuggestedChipCategory,
} from '@/components/ui/transcript-chips/types';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';

// ============================================================================
// Types
// ============================================================================

export interface ChipChatMapping {
  chipId: string;
  chatId: string;
  chipLabel: string;
  query: string;
  createdAt: number;
}

export interface SessionChipsState {
  /** Map of chipId to chatId for quick lookup */
  chipToChatMap: Record<string, string>;
  /** Ordered list of chip chats (for tab ordering) */
  chipChats: ChipChatMapping[];
  /** Currently active chip chat ID (for tab selection) */
  activeChipChatId: string | null;
}

// ============================================================================
// Atoms
// ============================================================================

const initialSessionChipsState: SessionChipsState = {
  chipToChatMap: {},
  chipChats: [],
  activeChipChatId: null,
};

/**
 * Per-session atom family for chip chat state.
 * Each session has its own independent set of chip-triggered chats.
 */
export const sessionChipsStateAtom = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_sessionId: string) => atom<SessionChipsState>(initialSessionChipsState)
);

/**
 * Atom to track if Evidence sidebar is showing chip tabs view vs regular chat.
 * When true, show the multi-tab interface; when false, show single chat.
 */
export const showChipTabsAtom = atom<boolean>(false);

// ============================================================================
// Derived Atoms
// ============================================================================

/**
 * Creates a derived atom to add a new chip chat to a session.
 */
export const addChipChatAtom = atom(
  null,
  (
    get,
    set,
    params: {
      sessionId: string;
      chipId: string;
      chatId: string;
      chipLabel: string;
      query: string;
    }
  ) => {
    const { sessionId, chipId, chatId, chipLabel, query } = params;
    const stateAtom = sessionChipsStateAtom(sessionId);
    const current = get(stateAtom);

    // Check if chip already has a chat
    if (current.chipToChatMap[chipId]) {
      // Just switch to that chat
      set(stateAtom, {
        ...current,
        activeChipChatId: current.chipToChatMap[chipId],
      });
      return current.chipToChatMap[chipId];
    }

    // Create new mapping
    const newMapping: ChipChatMapping = {
      chipId,
      chatId,
      chipLabel,
      query,
      createdAt: Date.now(),
    };

    set(stateAtom, {
      chipToChatMap: { ...current.chipToChatMap, [chipId]: chatId },
      chipChats: [...current.chipChats, newMapping],
      activeChipChatId: chatId,
    });

    return chatId;
  }
);

/**
 * Creates a derived atom to set the active chip chat.
 */
export const setActiveChipChatAtom = atom(
  null,
  (get, set, params: { sessionId: string; chatId: string | null }) => {
    const { sessionId, chatId } = params;
    const stateAtom = sessionChipsStateAtom(sessionId);
    const current = get(stateAtom);

    set(stateAtom, {
      ...current,
      activeChipChatId: chatId,
    });
  }
);

/**
 * Creates a derived atom to remove a chip chat.
 */
export const removeChipChatAtom = atom(
  null,
  (get, set, params: { sessionId: string; chipId: string }) => {
    const { sessionId, chipId } = params;
    const stateAtom = sessionChipsStateAtom(sessionId);
    const current = get(stateAtom);

    const chatId = current.chipToChatMap[chipId];
    if (!chatId) return;

    // Remove from maps - create new object without chipId key
    const restMap = Object.fromEntries(
      Object.entries(current.chipToChatMap).filter(([key]) => key !== chipId)
    );
    const filteredChats = current.chipChats.filter((c) => c.chipId !== chipId);

    // If active chat was removed, switch to first remaining or null
    const newActiveId =
      current.activeChipChatId === chatId
        ? (filteredChats[0]?.chatId ?? null)
        : current.activeChipChatId;

    set(stateAtom, {
      chipToChatMap: restMap,
      chipChats: filteredChats,
      activeChipChatId: newActiveId,
    });
  }
);

/**
 * Creates a derived atom to get chat ID for a chip.
 */
export const getChipChatIdAtom = atomFamily(
  (params: { sessionId: string; chipId: string }) =>
    atom((get) => {
      const stateAtom = sessionChipsStateAtom(params.sessionId);
      const state = get(stateAtom);
      return state.chipToChatMap[params.chipId] ?? null;
    })
);

// ============================================================================
// Shared Chips State for Sidebar Display
// ============================================================================

/**
 * Suggested chips from transcript analysis.
 * Set by SessionAskHeidiContainer, read by ChatInput in sidebar mode.
 */
export const suggestedChipsAtom = atom<SuggestedChip[]>([]);

/**
 * Pinned chips from user config.
 * Set by SessionAskHeidiContainer, read by ChatInput in sidebar mode.
 */
export const pinnedChipsAtom = atom<PinnedChip[]>([]);

/**
 * Whether transcription is currently active (for loading state).
 */
export const isTranscribingAtom = atom<boolean>(false);

/**
 * Chip click handler - stored so ChatInput can trigger chip queries.
 * This is a function atom, not a data atom.
 */
export const onChipClickAtom = atom<((chip: SuggestedChip) => void) | null>(null);

/**
 * Pinned chip click handler.
 */
export const onPinnedChipClickAtom = atom<((chip: PinnedChip) => void) | null>(null);

/**
 * Settings props for the SuggestionsPeekSheet in sidebar.
 * Contains all the config values and handlers needed for the settings popover.
 */
export interface ChipsSettingsProps {
  suggestEnabled: boolean;
  onSuggestEnabledChange?: (enabled: boolean) => void;
  autoAnswerPinned: boolean;
  onAutoAnswerPinnedChange?: (enabled: boolean) => void;
  enabledCategories: Set<SuggestedChipCategory>;
  onCategoryToggle?: (category: SuggestedChipCategory) => void;
  onAddPermanentChip?: (
    label: string,
    query: string,
    origin: 'library' | 'custom',
    description?: string,
    teamShortcutId?: string
  ) => void;
  onRemovePermanentChip?: (chipId: string) => void;
}

export const chipsSettingsAtom = atom<ChipsSettingsProps | null>(null);
